"use client"

import { useCallback, useEffect, useRef, useState } from "react"
import styles from "./TextArea.module.css"
import clsx from "clsx"

const MIN_ROWS = 3
const MAX_ROWS = 6

function getLineHeightPx(el: HTMLTextAreaElement): number {
  const style = getComputedStyle(el)
  const lineHeight = style.lineHeight
  const fontSize = parseFloat(style.fontSize)
  if (lineHeight === "normal") return fontSize * 1.2
  if (lineHeight.endsWith("px")) return parseFloat(lineHeight)
  return fontSize * parseFloat(lineHeight)
}

export default function TextArea({
  initText,
  placeholder,
  disabled,
  onSubmit,
  label,
  description,
  formId,
  allowEmptySubmit = false,
  onChange,
}: {
  initText: string
  placeholder: string
  disabled: boolean
  onSubmit: (newString: string) => Promise<string>
  label?: string
  description?: string
  /** When set, the form element gets this id so an external submit button can use form="formId" */
  formId?: string
  /** When true, onSubmit is called even when the field is empty (e.g. for "Mark as done" without message) */
  allowEmptySubmit?: boolean
  /** Called when the text value changes (e.g. to show/hide a send button) */
  onChange?: (value: string) => void
}) {
  const [text, setText] = useState(initText)
  const [loading, setLoading] = useState(false)
  const textareaRef = useRef<HTMLTextAreaElement>(null)

  const adjustHeight = useCallback(() => {
    const el = textareaRef.current
    if (!el) return
    const style = getComputedStyle(el)
    const lineHeightPx = getLineHeightPx(el)
    const paddingTop = parseFloat(style.paddingTop) || 0
    const paddingBottom = parseFloat(style.paddingBottom) || 0
    const paddingVertical = paddingTop + paddingBottom
    const prevMinHeight = el.style.minHeight
    el.style.minHeight = "0"
    el.style.height = "0"
    const contentHeight = el.scrollHeight - paddingVertical
    const rowsNeeded = Math.ceil(contentHeight / lineHeightPx)
    const rows = Math.min(MAX_ROWS, Math.max(MIN_ROWS, rowsNeeded))
    el.style.height = `${rows * lineHeightPx + paddingVertical}px`
    el.style.minHeight = prevMinHeight
  }, [])

  useEffect(() => {
    adjustHeight()
  }, [text, adjustHeight])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (loading) return
    if (!allowEmptySubmit && !text.trim()) return

    setLoading(true)
    try {
      const newText = await onSubmit(text)
      if (newText !== undefined) {
        setText(newText)
        onChange?.(newText)
      }
    } catch (error) {
      console.error("Error submitting input:", error)
    } finally {
      setLoading(false)
    }
  }

  const isDisabled = disabled || loading

  return (
    <div className={styles.container}>
      {label && (
        <span className={clsx("subHeadline", styles.label)}>{label}</span>
      )}
      {description && (
        <div className={clsx("subHeadline", styles.description)}>
          <span>{description}</span>
        </div>
      )}
      <form id={formId} onSubmit={handleSubmit} className={styles.form}>
        <textarea
          ref={textareaRef}
          name="inputField"
          placeholder={placeholder}
          value={text}
          onChange={(e) => {
            const value = e.target.value
            setText(value)
            onChange?.(value)
          }}
          disabled={isDisabled}
          autoComplete="off"
          className={clsx(
            "subHeadline",
            styles.textarea,
            isDisabled && styles.disabled,
          )}
        />
      </form>
    </div>
  )
}
